import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useRecordSponsorshipPayment } from "@/api/sponsorships";
import PropTypes from "prop-types";
import { currencies } from "@/lib/constants";

export function RecordPaymentModal({ isOpen, onClose, sponsorship }) {
  const [amount, setAmount] = useState("");
  const { mutate: recordPayment, isPending } = useRecordSponsorshipPayment();

  if (!sponsorship) return null;

  const remaining = sponsorship.amountPromised - sponsorship.paidAmount;
  const currencySymbol =
    currencies[sponsorship.currency || "INR"]?.symbol || "₹";

  const handleSubmit = (e) => {
    e.preventDefault();
    const parsedAmount = parseInt(amount, 10);
    if (!parsedAmount || parsedAmount <= 0) return;

    recordPayment(
      { id: sponsorship._id, amount: parsedAmount },
      {
        onSuccess: () => {
          setAmount("");
          onClose();
        },
      },
    );
  };

  const handlePayFull = () => {
    setAmount(remaining.toString());
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
          <DialogDescription>
            Record a payment against sponsorship{" "}
            <span className="font-semibold">{sponsorship.sponsorshipId}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Sponsorship Summary */}
          <div className="rounded-lg border p-4 space-y-2 bg-muted/50">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Sponsor</span>
              <span className="font-medium">{sponsorship.sponsorName}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Type</span>
              <span className="font-medium">
                {sponsorship.type}{" "}
                {sponsorship.type === "Mahallu"
                  ? `(${sponsorship.mahalluName})`
                  : `(${sponsorship.place})`}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Amount Promised</span>
              <span className="font-medium">
                {currencySymbol}
                {sponsorship.amountPromised.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Already Paid</span>
              <span className="font-medium text-green-600">
                {currencySymbol}
                {sponsorship.paidAmount.toLocaleString()}
              </span>
            </div>
            <div className="border-t pt-2 flex justify-between text-sm">
              <span className="text-muted-foreground font-medium">
                Remaining
              </span>
              <span className="font-bold text-orange-600">
                {currencySymbol}
                {remaining.toLocaleString()}
              </span>
            </div>
          </div>

          {remaining > 0 ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Payment Amount</label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Enter amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    max={remaining}
                    min={1}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handlePayFull}
                  >
                    Pay Full ({currencySymbol}
                    {remaining.toLocaleString()})
                  </Button>
                </div>
                {amount && parseInt(amount, 10) > remaining && (
                  <p className="text-xs text-red-500">
                    Amount cannot exceed remaining balance ({currencySymbol}
                    {remaining.toLocaleString()})
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={
                  isPending ||
                  !amount ||
                  parseInt(amount, 10) <= 0 ||
                  parseInt(amount, 10) > remaining
                }
              >
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Recording Payment...
                  </>
                ) : (
                  "Record Payment"
                )}
              </Button>
            </form>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-green-600 font-medium">
                This sponsorship has been fully paid!
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

RecordPaymentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sponsorship: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    sponsorshipId: PropTypes.string.isRequired,
    sponsorName: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    mahalluName: PropTypes.string,
    place: PropTypes.string,
    amountPromised: PropTypes.number.isRequired,
    paidAmount: PropTypes.number.isRequired,
    currency: PropTypes.string,
  }),
};
